import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { map } from 'rxjs';
import { AuthService } from './auth.service';
import { safeAuthenticatedReturnUrl } from './auth-navigation';

const PASSWORD_CHANGE_PATHS = new Set(['/auth/change-password', '/settings/change-password']);

function isPasswordChangePath(state: RouterStateSnapshot): boolean {
  return PASSWORD_CHANGE_PATHS.has(state.url.split('?')[0]);
}

export const authGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.waitForInitialization().pipe(
    map((bootstrapState) => {
      if (!auth.isAuthenticated()) {
        if (bootstrapState === 'retryable-offline') {
          return router.createUrlTree(['/auth/session-retry'], {
            queryParams: { returnUrl: state.url },
          });
        }
        return router.createUrlTree(['/auth/login'], {
          queryParams: { returnUrl: state.url },
        });
      }
      if (auth.passwordChangeRequired() && !isPasswordChangePath(state)) {
        return router.createUrlTree(['/auth/change-password'], {
          queryParams: { returnUrl: state.url },
        });
      }
      return true;
    }),
  );
};

export const guestGuard: CanActivateFn = (route) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.waitForInitialization().pipe(
    map(() => {
      if (!auth.isAuthenticated()) return true;

      const returnUrl = safeAuthenticatedReturnUrl(router, route.queryParamMap.get('returnUrl'));
      if (auth.passwordChangeRequired()) {
        return router.createUrlTree(['/auth/change-password'], {
          queryParams: { returnUrl },
        });
      }
      return router.parseUrl(returnUrl);
    }),
  );
};
